import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const BubbleContainer = styled.div`
  position: fixed;
  bottom: 30px;
  left: 30px;
  z-index: 2000;
  cursor: pointer;
  
  @media (max-width: 768px) {
    bottom: 20px;
    left: 20px;
  }
`;

const InlineSpeechBubble = styled(motion.div)`
  position: absolute;
  bottom: 120px;
  left: 0;
  background: white;
  border: 3px solid #ff6b6b;
  border-radius: 20px;
  padding: 12px 16px;
  max-width: 200px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  font-size: 0.9rem;
  font-weight: bold;
  color: #333;
  text-align: center;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const FloatingBubbles = ({ onCandleClick, onMugClick, isMugOverlayOpen, isCandlesOverlayOpen }) => {
  const [currentItem, setCurrentItem] = useState('candle'); // 'candle' or 'mug'
  const [isVisible, setIsVisible] = useState(false);
  const [showBubble, setShowBubble] = useState(false);

  useEffect(() => {
    let showTimer, hideTimer, bubbleTimer, switchTimer;

    const showItem = () => {
      setIsVisible(true);
      // Show speech bubble after item appears
      bubbleTimer = setTimeout(() => {
        setShowBubble(true);
      }, 1000);
    };

    const hideItem = () => {
      setShowBubble(false);
      setTimeout(() => {
        setIsVisible(false);
      }, 500);
    };

    const switchItem = () => {
      hideItem();
      // Switch to the other item after hiding
      switchTimer = setTimeout(() => {
        setCurrentItem(currentItem === 'candle' ? 'mug' : 'candle');
        showItem();
      }, 500);
    };

    // Initial show with candle
    showTimer = setTimeout(showItem, 0);

    // Set up recurring cycle - switch every 5 seconds
    const cycleTimer = setInterval(() => {
      switchItem();
    }, 5000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(bubbleTimer);
      clearTimeout(switchTimer);
      clearInterval(cycleTimer);
    };
  }, [currentItem]);

  const handleClick = () => {
    if (currentItem === 'candle') {
      onCandleClick();
    } else {
      onMugClick();
    }
    setShowBubble(false);
    setIsVisible(false);
  };

  // Don't show if the corresponding overlay is open
  if ((currentItem === 'candle' && isMugOverlayOpen) || 
      (currentItem === 'mug' && isCandlesOverlayOpen)) {
    return null;
  }

  const getItemData = () => {
    if (currentItem === 'candle') {
      return {
        image: "/assets/products/unscentedCandle/unscentedCandle.png",
        alt: "Candle",
        bubbleText: "Smell My Coop! 🕯️"
      };
    } else {
      return {
        image: "/assets/products/floatingMug/white-ceramic-mug-with-color-inside-red-11-oz-right-68b4c0b0a1a87.png",
        alt: "Mug",
        bubbleText: "Mug Collection ☕"
      };
    }
  };

  const itemData = getItemData();

  return (
    <AnimatePresence>
      {isVisible && (
        <BubbleContainer>
          <motion.div
            key={currentItem}
            initial={{ opacity: 0, scale: 0, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0, y: 50 }}
            transition={{ duration: 0.5, type: "spring", damping: 15 }}
            onClick={handleClick}
            whileHover={{ 
              scale: 1.1,
              rotate: 5
            }}
            whileTap={{ scale: 0.9 }}
          >
            <motion.img
              src={itemData.image}
              alt={itemData.alt}
              style={{
                width: '80px',
                height: '80px',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
              }}
              animate={{
                y: [0, -10, 0]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            
            <AnimatePresence>
              {showBubble && (
                <InlineSpeechBubble
                  initial={{ opacity: 0, scale: 0.5, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.5, y: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  {itemData.bubbleText}
                </InlineSpeechBubble>
              )}
            </AnimatePresence>
          </motion.div>
        </BubbleContainer>
      )}
    </AnimatePresence>
  );
};

export default FloatingBubbles;
